import type { ProjectCardProject } from '../components/sections/ProjectCard';
import type { CatalogProgramGroup } from './commercialisationLotsVitrine';
import {
  normalizeAvanceeTimelineSteps,
  resolveAvanceeCatalogBannerLabel,
} from './vitrineAvanceeTimeline';
import { resolveListingSurfaceRange } from './vitrineSurfaceRange';
import { parseVitrineUnitsCountField } from './vitrineUnitsCountField';
import { isActivitesVitrineZone, normalizeVitrineZoneKey } from './vitrineProgrammeUnits';

/** Même libellés que Provéa (`SiteWebVitrineView` → `typeLabel`). */
export function publicationProgramTypeLabel(
  zoneType?: string | null,
  classification?: string | null,
): string {
  const z = String(zoneType || classification || '').toLowerCase();
  if (z.includes('bureau')) return 'Bureaux';
  if (z.includes('maison')) return 'Maison';
  if (z.includes('collectif') || z.includes('immeuble')) return 'Appartement';
  if (z.includes('activit')) return 'Locaux d\'activité';
  if (z.includes('terrain')) return 'Terrain à bâtir';
  return 'Programme';
}

function projectTypeFromZone(zoneType?: string | null): ProjectCardProject['type'] {
  const z = normalizeVitrineZoneKey(zoneType);
  if (z.includes('bureau')) return 'bureaux';
  if (isActivitesVitrineZone(zoneType)) return 'activites';
  if (z.includes('maison')) return 'maison';
  if (z.includes('collectif') || z.includes('immeuble')) return 'collectif';
  return 'terrain';
}

/** Carte accueil / catalogue — alignée sur `SiteWebVitrineCatalogCard` (Provéa). */
export function catalogProgramGroupToLaunchCard(
  group: CatalogProgramGroup,
  _index = 0,
): ProjectCardProject {
  const { program, lots, programInfo } = group;
  const c = group.vitrineContent;
  const zoneType = group.zoneType ?? null;
  const classification = group.classification ?? null;

  const lotSurfaces = lots.map((lot) => lot.surface).filter((s) => s > 0);
  const range = resolveListingSurfaceRange({
    vitrineContent: c,
    surfaceMinM2: c?.surfaceMinM2 ?? program.surfaceMinM2,
    surfaceMaxM2: c?.surfaceMaxM2 ?? program.surfaceMaxM2,
    surface: c?.surface ?? program.surface,
  });
  let surfaceMinM2 = range.min ?? undefined;
  let surfaceMaxM2 = range.max ?? undefined;
  if (surfaceMinM2 == null && lotSurfaces.length > 0) {
    surfaceMinM2 = Math.min(...lotSurfaces);
    surfaceMaxM2 = Math.max(...lotSurfaces);
  }

  const title = (c?.title?.trim() || programInfo.name || program.title || 'Programme').trim();
  const image =
    c?.illustrationUrl?.trim() || programInfo.image || program.images[0] || '';
  const streetAddress = (c?.location?.trim() || programInfo.location || '').trim();
  const tags = Array.isArray(c?.tags)
    ? (c?.tags ?? []).map(String).map((t) => t.trim()).filter(Boolean)
    : program.tags;
  const dateLivraison =
    c?.dateLivraison?.trim() || programInfo.deliveryDate || program.availability || undefined;
  const avanceeTimeline = normalizeAvanceeTimelineSteps(c?.avanceeTimeline, dateLivraison);

  const statusLabel =
    c?.catalogStatusLabel?.trim() ||
    resolveAvanceeCatalogBannerLabel(c?.avanceeTimeline, dateLivraison) ||
    undefined;
  const promoFromTags = tags.find((label) =>
    /offert|notaire|promo|lancement|sp[ée]ciale|dispositif/i.test(label),
  );
  const promoLabel = c?.hideCatalogPromoBadge
    ? undefined
    : c?.catalogPromoLabel?.trim() || promoFromTags || tags[0] || undefined;

  const prices = lots.map((lot) => lot.price).filter((p) => p > 0);

  const unitsFromContent = parseVitrineUnitsCountField(c?.logements);
  const unitsDisplay =
    unitsFromContent.display ?? program.unitsCountDisplay ?? undefined;
  const unitsInteger =
    unitsFromContent.integerCount ??
    (typeof program.rooms === 'number' ? program.rooms : undefined);

  return {
    id: program.programKey?.trim() || program.id,
    title,
    description: c?.description?.trim() || program.description,
    image,
    type: projectTypeFromZone(zoneType),
    programTypeLabel:
      c?.catalogProgramTypeLabel?.trim() ||
      publicationProgramTypeLabel(zoneType, classification),
    status: 'en-cours',
    commune: program.location.city,
    streetAddress,
    surface: surfaceMinM2 ?? program.surface ?? 0,
    surfaceMinM2,
    surfaceMaxM2:
      surfaceMaxM2 != null && surfaceMinM2 != null && surfaceMaxM2 > surfaceMinM2
        ? surfaceMaxM2
        : undefined,
    logements: unitsInteger,
    logementsDisplay: unitsDisplay,
    zoneType,
    classification,
    dateLivraison,
    tags,
    avanceeTimeline,
    statusLabel,
    promoLabel,
    priceMin: prices.length > 0 ? Math.min(...prices) : undefined,
    priceMax: prices.length > 0 ? Math.max(...prices) : undefined,
    location: program.location.city,
    conditionLabel: 'Neuf',
  };
}
